// ecommerce-frontend/src/pages/RegisterPage.jsx

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  UserPlus, User, Mail, Lock, Phone, MapPin, ShoppingBag, Store, 
  AlertCircle, CheckCircle, Eye, EyeOff, FileText, ArrowRight, X 
} from 'lucide-react';
import { authAPI } from '../services/api';

// Styles d'animation CSS
const styles = `
  @keyframes shake {
    0%, 100% { transform: translateX(0); }
    10%, 30%, 50%, 70%, 90% { transform: translateX(-10px); }
    20%, 40%, 60%, 80% { transform: translateX(10px); }
  }

  @keyframes bounce {
    0%, 100% { transform: translateY(0); }
    50% { transform: translateY(-10px); }
  }

  @keyframes wiggle {
    0%, 100% { transform: rotate(0deg); }
    25% { transform: rotate(-5deg); }
    75% { transform: rotate(5deg); }
  }

  @keyframes pulse-glow {
    0%, 100% { box-shadow: 0 0 20px rgba(251, 146, 60, 0.5); }
    50% { box-shadow: 0 0 40px rgba(251, 146, 60, 0.8); }
  }

  .shake-animation {
    animation: shake 0.5s ease-in-out;
  }

  .bounce-animation {
    animation: bounce 0.6s ease-in-out infinite;
  }

  .wiggle-animation {
    animation: wiggle 0.5s ease-in-out;
  }

  .pulse-glow-animation {
    animation: pulse-glow 2s ease-in-out infinite;
  }

  .icon-container {
    transition: transform 0.3s ease;
  }

  .icon-container:hover {
    transform: scale(1.1) rotate(5deg);
  }
`;

// --- Contenu des Conditions Générales (Simulé) ---
const ConditionsGenerales = () => (
  <div className="p-6 h-[400px] overflow-y-auto bg-white/5 rounded-xl border border-white/20 text-gray-300 shadow-inner">
    <h3 className="text-xl font-bold text-orange-400 mb-4 sticky top-0 bg-gray-900/90 py-2 border-b border-white/10">
      Conditions Générales — MonMarket
    </h3>
    <div className='space-y-4'>
      <section>
        <h4 className="font-semibold text-white">1. Présentation de MonMarket</h4>
        <p className="text-sm">
          MonMarket est une plateforme en ligne permettant la mise en relation entre vendeurs et acheteurs pour l'achat de divers produits. Notre objectif est de faciliter l'accès à une large gamme d'articles proposés par des vendeurs indépendants.
        </p>
      </section>
      <section>
        <h4 className="font-semibold text-white">2. Rôle de MonMarket</h4>
        <p className="text-sm">
          MonMarket n'est ni vendeur, ni acheteur, ni propriétaire des produits listés. Nous ne stockons aucun article et n'intervenons pas en tant qu'intermédiaire officiel dans les transactions. Nous mettons simplement à disposition un espace permettant aux vendeurs de publier leurs produits et aux acheteurs d'entrer en contact ou d'acheter via la plateforme.
        </p>
      </section>
      <section>
        <h4 className="font-semibold text-white">3. Limites de responsabilité</h4>
        <p className="text-sm space-y-2">
          MonMarket ne peut être tenu responsable :
          <ul className="list-disc ml-5 mt-1 space-y-1">
            <li>Des fraudes, escroqueries ou fausses déclarations commises par des vendeurs ou acheteurs ;</li>
            <li>De la qualité, conformité ou authenticité des produits vendus ;</li>
            <li>D'un litige entre vendeur et acheteur ;</li>
            <li>D'un paiement effectué en dehors des procédures sécurisées de la plateforme ;</li>
            <li>De tout dommage ou perte résultant de l'utilisation du site ou d'une transaction entre utilisateurs.</li>
          </ul>
        </p>
      </section>
      <section>
        <h4 className="font-semibold text-white">4. Engagements des utilisateurs</h4>
        <p className="text-sm space-y-2">
          En utilisant MonMarket, vous vous engagez à :
          <ul className="list-disc ml-5 mt-1 space-y-1">
            <li>Fournir des informations exactes et à jour ;</li>
            <li>Ne pas publier de produits illégaux, contrefaits ou dangereux ;</li>
            <li>Respecter les lois en vigueur et les règles de la plateforme ;</li>
            <li>Agir avec honnêteté lors des achats et ventes.</li>
          </ul>
        </p>
      </section>
      <section>
        <h4 className="font-semibold text-white">5. Traitement des données</h4>
        <p className="text-sm">
          MonMarket collecte uniquement les données nécessaires à la création de compte, la gestion des annonces et la mise en relation entre vendeurs et acheteurs. Vos informations sont traitées de manière confidentielle et ne sont jamais revendues.
        </p>
      </section>
    </div>
  </div>
);

export default function RegisterPage() {
  const navigate = useNavigate();
  const isMounted = useRef(true);
  
  const [step, setStep] = useState(1); 
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    password2: '',
    phone: '',
    address: '',
    user_type: 'acheteur'
  });
  const [acceptedTerms, setAcceptedTerms] = useState(false); 
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showPassword2, setShowPassword2] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState({ score: 0, message: '', color: '' });
  const [statusMessage, setStatusMessage] = useState({ type: null, text: '' }); 
  const [shakeIcon, setShakeIcon] = useState(false);

  // Nettoyage au démontage du composant
  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    
    if (name === 'password') {
      evaluatePasswordStrength(value);
    }
    
    if (errors[name]) {
      setErrors({ ...errors, [name]: '' });
    }
    if (statusMessage.type) setStatusMessage({ type: null, text: '' });
  };
  
  const handleNextStep = () => {
    if (statusMessage.type) setStatusMessage({ type: null, text: '' });
    
    if (step === 1 && formData.user_type) {
      setErrors({});
      setStep(2); 
    } else if (step === 2 && acceptedTerms) {
      setErrors({});
      setStep(3); 
    } else if (step === 3 && validate()) {
      handleSubmit(); 
    }
  };

  const evaluatePasswordStrength = (password) => {
    let score = 0;
    let message = '';
    let color = '';

    if (password.length === 0) {
      setPasswordStrength({ score: 0, message: '', color: '' });
      return;
    }

    const hasLength = password.length >= 8;
    const hasUpperCase = /[A-Z]/.test(password);
    const hasLowerCase = /[a-z]/.test(password);
    const hasNumber = /[0-9]/.test(password);
    const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password);
    const notOnlyNumbers = !/^\d+$/.test(password);

    if (hasLength) score += 1;
    if (hasUpperCase) score += 1;
    if (hasLowerCase) score += 1;
    if (hasNumber) score += 1;
    if (hasSpecialChar) score += 1;
    if (notOnlyNumbers) score += 1;

    if (score <= 2) {
      message = 'Faible';
      color = 'red';
    } else if (score <= 4) {
      message = 'Moyen';
      color = 'yellow';
    } else {
      message = 'Fort';
      color = 'green';
    }

    setPasswordStrength({ score, message, color });
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.username || formData.username.length < 3) {
      newErrors.username = 'Le nom d\'utilisateur doit contenir au moins 3 caractères';
    }

    if (!formData.email || !/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email invalide';
    }

    if (!formData.password || formData.password.length < 8) {
      newErrors.password = 'Le mot de passe doit contenir au moins 8 caractères';
    } else if (/^\d+$/.test(formData.password)) {
      newErrors.password = 'Le mot de passe ne peut pas être entièrement numérique';
    } else if (passwordStrength.score < 3) {
      newErrors.password = 'Le mot de passe est trop faible. Utilisez des majuscules, minuscules, chiffres et caractères spéciaux';
    }

    if (formData.password !== formData.password2) {
      newErrors.password2 = 'Les mots de passe ne correspondent pas';
    }

    if (!formData.phone || formData.phone.length < 10) {
      newErrors.phone = 'Numéro de téléphone invalide';
    }
    
    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) {
      setShakeIcon(true);
      setTimeout(() => {
        if (isMounted.current) {
          setShakeIcon(false);
        }
      }, 500);
    }
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (statusMessage.type) setStatusMessage({ type: null, text: '' });
    setLoading(true);
    setErrors({});

    try {
      await authAPI.register(formData);
      
      // Vérifier si le composant est encore monté
      if (isMounted.current) {
        setStatusMessage({ 
          type: 'success', 
          text: 'Compte créé avec succès ! Vous pouvez maintenant vous connecter en utilisant votre Nom d\'utilisateur et votre Mot de passe.'
        });
        
        // Navigation sécurisée avec délai
        setTimeout(() => {
          if (isMounted.current) {
            navigate('/login', { replace: true });
          }
        }, 3000);
      }

    } catch (err) {
      // Vérifier avant chaque setState
      if (!isMounted.current) return;
      
      let generalError = 'Erreur lors de l\'inscription. Veuillez réessayer.';

      if (err.response && err.response.data) {
        const apiErrors = err.response.data;
        const newErrors = {};
        
        const errorMessages = {
          'password_too_common': 'Ce mot de passe est trop courant. Choisissez un mot de passe plus unique.',
          'password_entirely_numeric': 'Le mot de passe ne peut pas être entièrement numérique.',
          'password_too_short': 'Le mot de passe doit contenir au moins 8 caractères.',
          'password_too_similar': 'Le mot de passe est trop similaire à vos informations personnelles.',
          'This field must be unique.': 'Cette valeur est déjà utilisée.',
          'A user with that username already exists.': 'Ce nom d\'utilisateur existe déjà.',
          'user with this email already exists.': 'Cet email est déjà utilisé.'
        };
        
        Object.keys(apiErrors).forEach(key => {
          if (Array.isArray(apiErrors[key])) {
            const errorArray = apiErrors[key];
            errorArray.forEach(error => {
              const errorStr = typeof error === 'object' ? error.message || error.string : error;
              
              let translatedError = errorStr;
              Object.keys(errorMessages).forEach(pattern => {
                if (errorStr.includes(pattern) || errorStr.toLowerCase().includes(pattern.toLowerCase())) {
                  translatedError = errorMessages[pattern];
                }
              });
              
              newErrors[key] = translatedError;
            });
          } else {
            newErrors[key] = apiErrors[key];
          }
        });
        
        setErrors(newErrors);
        
        if (Object.keys(newErrors).length > 0) {
          generalError = 'Veuillez corriger les erreurs ci-dessous.';
        }
      } 
      
      setStatusMessage({ type: 'error', text: generalError });
      setErrors(prev => ({ ...prev, general: generalError }));
      setShakeIcon(true);
      setTimeout(() => {
        if (isMounted.current) {
          setShakeIcon(false);
        }
      }, 500);
      
    } finally {
      if (isMounted.current) {
        setLoading(false);
      }
    }
  };

  const PasswordStrengthBar = () => {
    if (!formData.password) return null;

    const strengthColors = {
      red: 'bg-red-400',
      yellow: 'bg-yellow-400',
      green: 'bg-green-400'
    };

    return (
      <div className="mt-2">
        <div className="flex gap-1 mb-1">
          {[1, 2, 3, 4, 5].map((level) => (
            <div
              key={level}
              className={`h-1 flex-1 rounded ${
                level <= passwordStrength.score
                  ? strengthColors[passwordStrength.color]
                  : 'bg-white/20'
              }`}
            />
          ))}
        </div>
        <p className={`text-xs text-${passwordStrength.color}-300`}>
          Force : {passwordStrength.message}
        </p>
      </div>
    );
  };

  const getStepHeader = () => {
    switch (step) {
      case 1:
        return { icon: <UserPlus size={36} className="text-white" />, title: "Type de compte", subtitle: "Choisissez votre rôle sur MonMarket" };
      case 2:
        return { icon: <FileText size={36} className="text-white" />, title: "Conditions Générales", subtitle: "Lisez et acceptez nos conditions d'utilisation" };
      case 3:
        return { icon: <UserPlus size={36} className="text-white" />, title: "Vos informations", subtitle: "Finalisez votre inscription" };
      default:
        return { icon: <UserPlus size={36} className="text-white" />, title: "Créer un compte", subtitle: "Rejoignez MonMarket dès maintenant" };
    }
  };
  const header = getStepHeader();

  const renderStepContent = () => {
    switch (step) {
      case 1:
        return (
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-200 mb-3">
              Je veux m'inscrire en tant que :
            </label>
            <div className="grid grid-cols-2 gap-4">
              <button
                type="button"
                onClick={() => setFormData({ ...formData, user_type: 'acheteur' })}
                className={`p-4 rounded-2xl border-2 transition-all backdrop-blur-md ${
                  formData.user_type === 'acheteur'
                    ? 'border-orange-400 bg-orange-500/20'
                    : 'border-white/20 bg-white/5 hover:border-orange-400/50'
                }`}
              >
                <ShoppingBag size={32} className={`mx-auto mb-2 ${formData.user_type === 'acheteur' ? 'text-orange-400 bounce-animation' : 'text-gray-400'}`} />
                <div className="font-semibold text-white">Acheteur</div>
                <div className="text-xs text-gray-400">J'achète des produits</div>
              </button>

              <button
                type="button"
                onClick={() => setFormData({ ...formData, user_type: 'vendeur' })}
                className={`p-4 rounded-2xl border-2 transition-all backdrop-blur-md ${
                  formData.user_type === 'vendeur'
                    ? 'border-orange-400 bg-orange-500/20'
                    : 'border-white/20 bg-white/5 hover:border-orange-400/50'
                }`}
              >
                <Store size={32} className={`mx-auto mb-2 ${formData.user_type === 'vendeur' ? 'text-orange-400 bounce-animation' : 'text-gray-400'}`} />
                <div className="font-semibold text-white">Vendeur</div>
                <div className="text-xs text-gray-400">Je vends des produits</div>
              </button>
            </div>
            {errors.user_type && <p className="text-red-300 text-sm mt-3 text-center shake-animation">{errors.user_type}</p>}
          </div>
        );

      case 2:
        return (
          <div className="space-y-6">
            <ConditionsGenerales />
            <div className="flex items-start">
              <input
                type="checkbox"
                id="terms"
                checked={acceptedTerms}
                onChange={(e) => setAcceptedTerms(e.target.checked)}
                className="w-4 h-4 text-orange-500 bg-white/10 border-white/20 rounded focus:ring-orange-400 mt-1"
              />
              <label htmlFor="terms" className="ml-3 text-sm text-gray-300 select-none">
                J'ai lu et j'accepte les conditions d'utilisation et la politique de confidentialité de MonMarket.
              </label>
            </div>
          </div>
        );
        
      case 3:
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-200 mb-2">Nom d'utilisateur *</label>
              <div className="relative">
                <User className={`absolute left-4 top-4 text-gray-400 ${errors.username ? 'wiggle-animation' : ''}`} size={20} />
                <input
                  type="text"
                  name="username"
                  value={formData.username}
                  onChange={handleChange}
                  className={`w-full pl-12 pr-4 py-4 bg-white/10 backdrop-blur-md border rounded-2xl focus:ring-2 focus:ring-orange-400 focus:border-transparent text-white placeholder-gray-400 transition-all ${
                    errors.username ? 'border-red-400' : 'border-white/20'
                  }`}
                  placeholder="Choisissez un nom d'utilisateur"
                />
                {!errors.username && formData.username.length >= 3 && (<CheckCircle className="absolute right-4 top-4 text-green-400" size={20} />)}
              </div>
              {errors.username && <p className="text-red-300 text-sm mt-1 shake-animation">{errors.username}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-200 mb-2">Email *</label>
              <div className="relative">
                <Mail className={`absolute left-4 top-4 text-gray-400 ${errors.email ? 'wiggle-animation' : ''}`} size={20} />
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className={`w-full pl-12 pr-4 py-4 bg-white/10 backdrop-blur-md border rounded-2xl focus:ring-2 focus:ring-orange-400 focus:border-transparent text-white placeholder-gray-400 transition-all ${
                    errors.email ? 'border-red-400' : 'border-white/20'
                  }`}
                  placeholder="votre.email@exemple.com"
                />
                {!errors.email && /\S+@\S+\.\S+/.test(formData.email) && (<CheckCircle className="absolute right-4 top-4 text-green-400" size={20} />)}
              </div>
              {errors.email && <p className="text-red-300 text-sm mt-1 shake-animation">{errors.email}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-200 mb-2">Téléphone *</label>
              <div className="relative">
                <Phone className={`absolute left-4 top-4 text-gray-400 ${errors.phone ? 'wiggle-animation' : ''}`} size={20} />
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  className={`w-full pl-12 pr-4 py-4 bg-white/10 backdrop-blur-md border rounded-2xl focus:ring-2 focus:ring-orange-400 focus:border-transparent text-white placeholder-gray-400 transition-all ${
                    errors.phone ? 'border-red-400' : 'border-white/20'
                  }`}
                  placeholder="+225 07 12 34 56 78"
                />
              </div>
              {errors.phone && <p className="text-red-300 text-sm mt-1 shake-animation">{errors.phone}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-200 mb-2">Adresse</label>
              <div className="relative">
                <MapPin className="absolute left-4 top-4 text-gray-400" size={20} />
                <input
                  type="text"
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  className="w-full pl-12 pr-4 py-4 bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl focus:ring-2 focus:ring-orange-400 focus:border-transparent text-white placeholder-gray-400 transition-all"
                  placeholder="Cocody, Abidjan"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-200 mb-2">Mot de passe *</label>
                <div className="relative">
                  <Lock className={`absolute left-4 top-4 text-gray-400 ${errors.password ? 'wiggle-animation' : ''}`} size={20} />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    className={`w-full pl-12 pr-12 py-4 bg-white/10 backdrop-blur-md border rounded-2xl focus:ring-2 focus:ring-orange-400 focus:border-transparent text-white placeholder-gray-400 transition-all ${
                      errors.password ? 'border-red-400' : 'border-white/20'
                    }`}
                    placeholder="••••••••"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-4 top-4 text-gray-400 hover:text-white transition"
                  >
                    {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                  </button>
                </div>
                <PasswordStrengthBar />
                {errors.password && <p className="text-red-300 text-sm mt-1 shake-animation">{errors.password}</p>}
                <p className="text-xs text-gray-400 mt-1">
                  Min. 8 caractères, majuscules, minuscules, chiffres et symboles
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-200 mb-2">Confirmer *</label>
                <div className="relative">
                  <Lock className={`absolute left-4 top-4 text-gray-400 ${errors.password2 ? 'wiggle-animation' : ''}`} size={20} />
                  <input
                    type={showPassword2 ? 'text' : 'password'}
                    name="password2"
                    value={formData.password2}
                    onChange={handleChange}
                    className={`w-full pl-12 pr-12 py-4 bg-white/10 backdrop-blur-md border rounded-2xl focus:ring-2 focus:ring-orange-400 focus:border-transparent text-white placeholder-gray-400 transition-all ${
                      errors.password2 ? 'border-red-400' : 'border-white/20'
                    }`}
                    placeholder="••••••••"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword2(!showPassword2)}
                    className="absolute right-4 top-4 text-gray-400 hover:text-white transition"
                  >
                    {showPassword2 ? <EyeOff size={20} /> : <Eye size={20} />}
                  </button>
                  {!errors.password2 && formData.password2 && formData.password === formData.password2 && (
                    <CheckCircle className="absolute right-12 top-4 text-green-400" size={20} />
                  )}
                </div>
                {errors.password2 && <p className="text-red-300 text-sm mt-1 shake-animation">{errors.password2}</p>}
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };
  
  const getButtonContent = () => {
    if (step === 1) {
      return { text: "Continuer", icon: <ArrowRight size={22} className="ml-2" />, disabled: !formData.user_type, loadingText: "Préparation..." };
    } else if (step === 2) {
      return { text: "J'accepte et Continuer", icon: <ArrowRight size={22} className="ml-2" />, disabled: !acceptedTerms, loadingText: "Vérification..." };
    } else {
      const formValid = formData.username && formData.email && formData.password && formData.password2 && formData.phone;
      return { text: "Créer mon compte", icon: <UserPlus size={22} className="mr-2" />, disabled: loading || !formValid, loadingText: "Inscription..." };
    }
  }
  const buttonProps = getButtonContent();

  const renderAlert = (type, text) => {
    const isError = type === 'error' || errors.general;
    const alertClasses = isError ? 'bg-red-500/20 border-red-400/30' : 'bg-green-500/20 border-green-400/30';
    const icon = isError ? <AlertCircle size={20} className="text-red-300 mr-3 flex-shrink-0" /> : <CheckCircle size={20} className="text-green-300 mr-3 flex-shrink-0" />;
    const textColor = isError ? 'text-red-100' : 'text-green-100';

    return (
      <div className={`backdrop-blur-md border rounded-2xl p-4 mb-6 flex items-center justify-between ${alertClasses} ${isError ? 'shake-animation' : ''}`}>
        <div className="flex items-center">
            {icon}
            <span className={`text-sm ${textColor}`}>{text}</span>
        </div>
        <button onClick={() => setStatusMessage({ type: null, text: '' })} className={`p-1 rounded-full ${isError ? 'text-red-300 hover:bg-red-500/30' : 'text-green-300 hover:bg-green-500/30'} transition-colors`}>
            <X size={16} />
        </button>
      </div>
    );
  };

  return (
    <>
      <style>{styles}</style>
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-black flex items-center justify-center p-4 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-orange-500/20 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-white/10 rounded-full blur-3xl"></div>
        <div className="absolute top-1/3 right-1/3 w-[500px] h-[500px] bg-orange-400/10 rounded-full blur-3xl"></div>

        <div className="bg-white/10 backdrop-blur-2xl rounded-3xl shadow-2xl max-w-2xl w-full p-8 border border-white/20 relative z-10">
          <div className="text-center mb-8">
            <div className={`inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-orange-400 to-orange-600 rounded-3xl mb-4 shadow-xl icon-container ${shakeIcon ? 'shake-animation' : ''} ${statusMessage.type === 'success' ? 'pulse-glow-animation' : ''}`}>
              {header.icon}
            </div>
            <h2 className="text-4xl font-bold text-white mb-2">{header.title}</h2>
            <p className="text-gray-300">{header.subtitle}</p>

            <div className="flex justify-center space-x-2 mt-4">
              {[1, 2, 3].map((s) => (
                <div
                  key={s}
                  className={`w-1/4 h-1 rounded-full transition-colors ${
                    s === step ? 'bg-orange-500' : 'bg-gray-700'
                  }`}
                />
              ))}
            </div>
          </div>

          {statusMessage.text && renderAlert(statusMessage.type, statusMessage.text)}

          {renderStepContent()}

          <div className="mt-8 flex justify-between">
            {step > 1 && !statusMessage.type && (
              <button
                onClick={() => {
                  setErrors({});
                  setStep(step - 1);
                }}
                className="px-6 py-3 bg-white/10 text-white rounded-2xl font-semibold hover:bg-white/20 transition-all disabled:opacity-50"
                disabled={loading}
              >
                ← Retour
              </button>
            )}

            {!statusMessage.type && ( 
              <button
                onClick={handleNextStep}
                disabled={buttonProps.disabled || loading}
                className={`flex-grow ${step > 1 ? 'ml-4' : ''} bg-gradient-to-r from-orange-500 to-orange-600 text-white py-4 rounded-2xl font-bold text-lg hover:from-orange-600 hover:to-orange-700 transition-all shadow-xl hover:shadow-2xl transform hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center`}
              >
                {loading && step === 3 ? (
                  <>
                    <svg className="animate-spin h-5 w-5 mr-2" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    {buttonProps.loadingText}
                  </>
                ) : (
                  <>
                    {step === 3 && buttonProps.icon}
                    {buttonProps.text}
                    {step !== 3 && buttonProps.icon}
                  </>
                )}
              </button>
            )}
          </div>

          <div className="mt-6 text-center">
            <p className="text-gray-300">
              Déjà un compte ?{' '}
              <button onClick={() => navigate('/login')} className="text-orange-400 font-semibold hover:text-orange-300 transition">
                Se connecter
              </button>
            </p>
          </div>

          <div className="mt-4 text-center">
            <button onClick={() => navigate('/')} className="text-sm text-gray-400 hover:text-white transition">
              ← Retour à l'accueil
            </button>
          </div>
        </div>
      </div>
    </>
  );
}